(ns app.front.view.group.admin.branding.website
  (:require [app.domain.branding.model :as branding-model]
            [app.domain.call-to-action.model :as cta-model]
            [app.domain.custom-link.model :as custom-link-model]
            [app.front.entities :as entities]
            [app.front.file.upload :as upload]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :as futil]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.domain.validators :as validators]
            [core.front.carbon :as cc]
            [core.front.carbon-icons :as carbon-icons]
            [core.front.color :as color]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def ^:private cta-slot-count 3)

(defn- empty-cta-slot [order]
  {:enabled?                false
   :call-to-action/icon     :icon/information
   :call-to-action/title    {}
   :call-to-action/subtitle {}
   :call-to-action/content  {}
   :ordered/order           order})

(defn- server-cta->slot [cta]
  (assoc cta :enabled? true))

(defn- server-ctas->draft [server-ctas]
  (let [slots (mapv server-cta->slot server-ctas)]
    (into slots (map #(empty-cta-slot (inc %))
                     (range (count slots) cta-slot-count)))))

(defn- ctas-draft->payload [draft]
  (->> draft
       (filter :enabled?)
       (mapv #(dissoc % :enabled?))))

(rf/reg-sub ::editor :-> ::editor)

(rf/reg-sub ::value-in-editor
  :<- [::editor]
  (fn [editor [_ k]]
    (get editor k)))

(rf/reg-sub ::value-in-editor-or-group
  :<- [::editor]
  :<- [::gas/group]
  (fn [[editor group] [_ k]]
    (or (get editor k)
        (get group k))))

(rf/reg-sub ::values-have-been-edited?
  :<- [::editor]
  (fn [editor _]
    (some (comp some? val) editor)))

(rf/reg-sub ::header-texts-have-been-edited?
  (fn [db _]
    (some db [::landing-page-title ::landing-page-subtitle ::header-title])))

(rf/reg-sub ::landing-page-title :-> ::landing-page-title)
(rf/reg-sub ::landing-page-subtitle :-> ::landing-page-subtitle)
(rf/reg-sub ::header-title :-> ::header-title)

(def ^:private max-custom-links 3)

(defn- empty-custom-link [order]
  {:custom-link/url    ""
   :custom-link/titles {}
   :custom-link/icon   custom-link-model/default-custom-link-icon
   :ordered/order      order})

(rf/reg-sub ::images-have-been-added?
  (fn [{::keys [images]}]
    (boolean images)))

(rf/reg-sub ::image-has-been-added?
  (fn [{::keys [images]} [_ image]]
    (contains? images image)))

(rf/reg-sub ::draft-image-filename
  (fn [{::keys [images]} [_ type]]
    (get-in images [type :file :file/filename])))

(rf/reg-event-fx ::delete-branding.success
  (fn [{{:keys [branding]} :db} [_ status response]]
    {:fx (conj [[:dispatch [::entities/apply-changes status response]]
                [:dispatch [:branding/set-branding branding]]])}))

(rf/reg-event-fx ::clear
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::images ::images-to-remove ::editor ::landing-page-title ::landing-page-subtitle ::header-title)
     :dispatch [:branding/clear-branding-images]}))

(rf/reg-event-fx ::cancel-edits
  (fn [{:keys [db]} _]
    (let [initial-config (get db :branding/config-initial)]
      {:db (-> db
               (assoc :branding/config initial-config)
               (assoc ::ctas-draft (server-ctas->draft (::ctas-server db)))
               (assoc ::custom-links-draft (::custom-links-server db))
               (assoc ::dynamic-ctas-draft (::dynamic-ctas-server db))
               (update ::editor-generation (fnil inc 0)))
       :fx [[:dispatch [::clear]]
            [:dispatch [:branding/clear-branding-images]]
            [:branding/generate-css initial-config]]})))

(rf/reg-event-fx ::delete-branding
  (fn [{:keys [db]} [_ {group-id :db/id} category]]
    {:db (dissoc db ::editor)
     :fx [[:comm/command {:command    :group/delete-branding
                          :data       (util/mmap group-id category)
                          :on-success [::delete-branding.success]}]]}))

(rf/reg-event-fx ::set-value-in-editor
  (fn [{:keys [db]} [_ k v]]
    {:db       (assoc-in db [::editor k] v)
     :dispatch [:branding/update-config k v]}))

(rf/reg-event-db ::set-landing-page-title
  (fn [db [_ strings-map]]
    (assoc db ::landing-page-title strings-map)))

(rf/reg-event-db ::set-landing-page-subtitle
  (fn [db [_ strings-map]]
    (assoc db ::landing-page-subtitle strings-map)))

(rf/reg-event-db ::set-header-title
  (fn [db [_ strings-map]]
    (assoc db ::header-title strings-map)))

(rf/reg-sub ::editor-generation :-> ::editor-generation)

(rf/reg-sub ::custom-links-draft :-> ::custom-links-draft)
(rf/reg-sub ::custom-links-server :-> ::custom-links-server)

(defn- custom-links-edited? [db]
  (let [draft  (::custom-links-draft db)
        server (::custom-links-server db)]
    (and (some? draft)
         (not= draft server))))

(defn- custom-links-valid? [draft]
  (not (and (seq draft)
            (some custom-link-model/custom-link-invalid? draft))))

(rf/reg-sub ::custom-links-edited?
  :-> custom-links-edited?)

(rf/reg-sub ::custom-links-valid?
  (fn [db _]
    (custom-links-valid? (::custom-links-draft db))))

(rf/reg-event-db ::init-custom-links-draft
  (fn [db [_ custom-links]]
    (let [links (if (seq custom-links) (vec custom-links) [])]
      (assoc db ::custom-links-draft links
             ::custom-links-server links))))

(rf/reg-event-db ::add-custom-link
  (fn [db _]
    (let [draft (or (::custom-links-draft db) [])]
      (if (>= (count draft) max-custom-links)
        db
        (let [order (inc (count draft))]
          (update db ::custom-links-draft (fnil conj []) (empty-custom-link order)))))))

(rf/reg-event-db ::remove-custom-link
  (fn [db [_ idx]]
    (let [draft  (::custom-links-draft db)
          draft' (->> (into (subvec draft 0 idx) (subvec draft (inc idx)))
                      (map-indexed (fn [i link] (assoc link :ordered/order (inc i))))
                      vec)]
      (assoc db ::custom-links-draft draft'))))

(rf/reg-event-db ::set-custom-link-field
  (fn [db [_ idx field value]]
    (assoc-in db [::custom-links-draft idx field] value)))

(rf/reg-sub ::ctas-draft :-> ::ctas-draft)
(rf/reg-sub ::ctas-group-id :-> ::ctas-group-id)

(rf/reg-sub ::dynamic-ctas-draft :-> ::dynamic-ctas-draft)
(rf/reg-sub ::dynamic-ctas-server :-> ::dynamic-ctas-server)

(rf/reg-sub ::dynamic-ctas-group
  :<- [::gas/group]
  (fn [group _]
    (set (:group/dynamic-calls-to-action group))))

(rf/reg-sub ::dynamic-ctas-effective
  :<- [::dynamic-ctas-draft]
  :<- [::dynamic-ctas-server]
  :<- [::dynamic-ctas-group]
  (fn [[draft server-kinds group-kinds] _]
    (or draft server-kinds group-kinds #{})))

(rf/reg-sub ::dynamic-ctas-edited?
  :<- [::dynamic-ctas-draft]
  :<- [::dynamic-ctas-server]
  :<- [::dynamic-ctas-group]
  (fn [[draft server-kinds group-kinds] _]
    (and (some? draft)
         (not= draft (or server-kinds group-kinds)))))

(rf/reg-event-db ::init-dynamic-ctas
  (fn [db [_ kinds]]
    (let [kinds (set kinds)]
      (assoc db
             ::dynamic-ctas-server kinds
             ::dynamic-ctas-draft kinds))))

(rf/reg-sub ::ctas-edited?
  (fn [db _]
    (let [draft  (::ctas-draft db)
          server (::ctas-server db)]
      (and (some? draft)
           (not= draft (server-ctas->draft server))))))

(rf/reg-event-fx ::fetch-ctas
  (fn [_ [_ group-id]]
    {:comm/command {:command    :call-to-action/fetch-group-calls-to-action
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-ctas.success group-id]}}))

(rf/reg-event-db ::fetch-ctas.success
  (fn [db [_ group-id _status response]]
    (let [draft (server-ctas->draft response)]
      (assoc db ::ctas-server response ::ctas-draft draft ::ctas-group-id group-id))))

(rf/reg-event-db ::toggle-cta-slot
  (fn [db [_ idx enabled?]]
    (assoc-in db [::ctas-draft idx :enabled?] enabled?)))

(rf/reg-event-db ::set-cta-field
  (fn [db [_ idx field value]]
    (assoc-in db [::ctas-draft idx field] value)))

(rf/reg-event-db ::toggle-dynamic-cta
  (fn [db [_ current-kinds kind enabled?]]
    (let [current-kinds (set current-kinds)]
      (assoc db
             ::dynamic-ctas-draft
             (if enabled?
               (conj current-kinds kind)
               (disj current-kinds kind))))))

(rf/reg-event-fx ::save-ctas
  (fn [{:keys [db]} [_ group-id notify?]]
    {:comm/command (cond-> {:command        :call-to-action/update-group-calls-to-action
                            :data           {:group-id        group-id
                                             :calls-to-action (ctas-draft->payload (::ctas-draft db))}
                            :on-success     [::save-ctas.success]
                            :on-bad-request [::save-ctas.bad-request]}
                     notify?
                     (assoc :notify {:success :group.admin.branding/save-success}))}))

(rf/reg-event-fx ::save-ctas.success
  (fn [{:keys [db]} _]
    {:fx [[:dispatch [::fetch-ctas (::ctas-group-id db)]]]}))

(rf/reg-event-fx ::save-ctas.bad-request
  (fn [_ _event]
    (when-let [first-invalid-elt (.querySelector js/document "[data-invalid=true]")]
      (.focus first-invalid-elt))
    {:fx [[:dispatch [:notifications/add :toast :error :error/error :error/required-fields-missing]]]}))


(rf/reg-event-fx ::save-all
  (fn [{:keys [db]} [_ group-id]]
    (let [links-edited? (custom-links-edited? db)
          links-valid?  (custom-links-valid? (::custom-links-draft db))
          has-branding? (or (some db [::editor ::images ::images-to-remove
                                      ::landing-page-title ::landing-page-subtitle ::header-title])
                            links-edited?)
          draft         (::ctas-draft db)
          has-dynamic?  (not= (::dynamic-ctas-draft db)
                              (::dynamic-ctas-server db))
          has-ctas?     (and (some? draft)
                             (not= draft (server-ctas->draft (::ctas-server db))))]
      (when-not (and links-edited? (not links-valid?))
        {:fx (cond-> []
               (or has-branding? has-dynamic?) (conj [:dispatch [::save-branding-attributes group-id]])
               has-ctas?                       (conj [:dispatch [::save-ctas group-id (not (or has-branding? has-dynamic?))]]))}))))

;; -- Branding save/clear -------------------------------------------------------

(rf/reg-event-fx ::save-branding-attributes
  (fn [{:keys [db]} [_ group-id]]
    (let [{::keys [images images-to-remove editor landing-page-title landing-page-subtitle
                   header-title dynamic-ctas-draft]}                                       db
          custom-links                                                                     (when (custom-links-edited? db)
                                                                                             (::custom-links-draft db))]
      (when-not (and custom-links (not (custom-links-valid? custom-links)))
        {:comm/command {:command         :group/save-branding-attributes
                        :data            (cond-> {:group-id group-id
                                                  :data     (or (->> editor (p/remove-vals nil?)) {})}
                                           landing-page-title     (assoc :landing-page-title landing-page-title)
                                           landing-page-subtitle  (assoc :landing-page-subtitle landing-page-subtitle)
                                           header-title           (assoc :header-title header-title)
                                           custom-links           (assoc :custom-links custom-links)
                                           dynamic-ctas-draft     (assoc-in [:data :group/dynamic-calls-to-action]
                                                                            dynamic-ctas-draft)
                                           (seq images)           (assoc :images (map val images))
                                           (seq images-to-remove) (assoc :remove-images (mapv branding-model/image-type->attribute images-to-remove)))
                        :notify          {:success :group.admin.branding/save-success}
                        :on-success      [::save-branding-attributes.success group-id]
                        :on-server-error [::save-branding-attributes.fail]}}))))

(rf/reg-event-fx ::save-branding-attributes.success
  (fn [{:keys [db]} [_ group-id status response]]
    (let [saved-custom-links (::custom-links-draft db)]
      {:db (p/assoc-if (dissoc db ::images ::images-to-remove ::editor ::landing-page-title ::landing-page-subtitle ::header-title)
                       ;; Preserve custom-links draft+server as the just-saved value so that
                       ;; the section component does not re-init from stale branding config.
                       ::custom-links-draft saved-custom-links
                       ::custom-links-server saved-custom-links)
       :fx [[:dispatch [::entities/apply-changes status response]]
            [:dispatch [::init-dynamic-ctas (::dynamic-ctas-draft db)]]
            [:dispatch [::clear]]
            [:dispatch [:branding/reset-branding-images group-id]]]})))

(rf/reg-event-fx ::save-branding-attributes.fail
  (fn [_ _]
    {:dispatch [:notifications/add :toast :error :error/error :group.admin.branding/changes-not-saved]}))

(rf/reg-event-fx ::save-uri-path-id.success
  (fn [_ [_ path-id status response]]
    {:fx [[:dispatch [::entities/apply-changes status response]]
          (when-not path-id
            [:dispatch [:branding/force-branding branding-model/default-branding]])]}))

(rf/reg-event-fx ::save-uri-path-id
  (fn [_ [_ group-id path-id]]
    {:comm/command {:command    :group/set-uri-path-id
                    :data       (util/mmap group-id path-id)
                    :on-success [::save-uri-path-id.success path-id]}}))

(rf/reg-event-db ::set-image
  (fn [db [_ type file-id file]]
    (assoc-in db [::images type]
              (util/mmap {:attr (branding-model/image-type->attribute type)} file-id file))))

(rf/reg-event-fx ::clear-image
  (fn [{:keys [db]} [_ type]]
    {:db (-> db
             (update ::images dissoc type)
             (update ::images-to-remove (fnil conj #{}) type))
     :fx [[:dispatch [:branding/remove-image type]]]}))

(rf/reg-sub ::images-have-been-removed?
  (fn [{::keys [images-to-remove]}]
    (boolean (seq images-to-remove))))

(rf/reg-sub ::image-has-been-removed?
  (fn [{::keys [images-to-remove]} [_ type]]
    (contains? images-to-remove type)))

(rf/reg-event-db ::set-uri-path-id
  (fn [db [_ path-id]]
    (assoc db ::uri-path-id path-id)))

(rf/reg-sub ::get-uri-path-id :-> ::uri-path-id)

(rf/reg-sub ::uri-path-id
  :<- [::gas/group]
  :<- [::get-uri-path-id]
  (fn [[group uri-path-id] _]
    (or uri-path-id
        (:group/uri-path-id group))))

(rf/reg-sub ::header-contrast-ratio
  :<- [::value-in-editor-or-group :group/branding-banner-fg-color]
  :<- [::value-in-editor-or-group :group/branding-banner-bg-color]
  (fn [[fg-color bg-color] _]
    (when (and fg-color bg-color)
      (color/contrast-ratio fg-color bg-color))))

(defn color-editor [tr label-key attr-key]
  [:div.mr-4
   [:label.bx--label (tr [label-key])]
   [:br]
   [:input {:type      :color
            :value     (<sub [::value-in-editor-or-group attr-key])
            :on-change #(>evt [::set-value-in-editor attr-key (futil/tval %)])}]])

(defn contrast-warning
  "Displays a warning when contrast ratio between foreground and background colors
   does not meet WCAG AA accessibility guidelines (4.5:1 for normal text)."
  [tr]
  (let [ratio      (<sub [::header-contrast-ratio])
        wcag-level (when ratio (color/wcag-level ratio))]
    (when ratio
      [:div.mt-2
       (if (#{:aa :aaa} wcag-level)
         [:div.flex.items-center.gap-2.text-green-600
          [cc/icon {:icon        i/check
                    :description ""
                    :aria-hidden true}]
          [:span (tr [:group/branding-contrast-ok] [(futil/to-fixed ratio 1)])]]
         [cc/inline-notification
          {:kind              :warning
           :hide-close-button true
           :title             (tr [:group/branding-contrast-warning])
           :subtitle          (tr [:group/branding-contrast-warning-details]
                                  [(futil/to-fixed ratio 1)])}])])))

(defn image-upload [tr type & [{:keys [button-i18n-key remove-i18n-key group]}]]
  (let [filename   (or (<sub [::draft-image-filename type])
                       (some-> group (get (branding-model/image-type->attribute type)) :file/filename))
        has-image? (or filename (<sub [:branding/image-exists? type]))
        button     [upload/upload-button (str "upload-button-" type)
                    (p/assoc-if
                      {:tr             tr
                       :attach-file-fn (fn [file-id filename content-type md5]
                                         (>evt [:branding/replace-image type file-id])
                                         (>evt [::set-image type file-id
                                                {:file/filename     filename
                                                 :file/content-type content-type
                                                 :file/category     :file.category/branding-logo
                                                 :file/md5          md5}]))
                       :accept-types   [".png" ".svg" ".jpg" ".jpeg"]
                       :button-opts    {:kind :primary}}
                      :button-i18n-key button-i18n-key)]]
    (if has-image?
      [:div.flex.items-center.gap-4
       button
       (when filename [:span.font-bold filename])
       [ui/button {:kind     :secondary
                   :size     :small
                   :on-click #(>evt [::clear-image type])}
        (tr [(or remove-i18n-key :group/branding-landing-background-remove)])]]
      button)))

(defn delete-branding-button [tr group]
  [ui/modal-confirmation-button
   {:id                  "remove-email-branding"
    :trigger-button-opts {:kind :secondary
                          :size :small}
    :tr                  tr
    :trigger-button-text (tr [:group/remove-branding])
    :on-yes-click        #(>evt [::delete-branding group :website])}
   (tr [:group/really-remove-branding])])

(defn logo-upload
  "Logo upload section, shared between domain and superdomain branding."
  [tr group]
  [:div.mt-5
   [:label.bx--label (cond
                       (<sub [::image-has-been-added? :logo]) (tr [:group/branding-logo-remember-to-save])
                       (<sub [:branding/image-exists? :logo]) (tr [:group/branding-logo])
                       :else                                  (tr [:group/branding-no-logo]))]
   [image-upload tr :logo {:button-i18n-key :group/branding-logo-upload
                           :remove-i18n-key :group/branding-logo-remove
                           :group           group}]])

(defn header-color-settings [tr]
  [:div.mt-4
   [:div.flex.mt-5
    [color-editor tr :group/branding-header-fg-color :group/branding-banner-fg-color]
    [color-editor tr :group/branding-header-bg-color :group/branding-banner-bg-color]]
   [contrast-warning tr]])

(defn- image-upload-with-preview [tr type label]
  (let [removed? (<sub [::image-has-been-removed? type])
        img-url  (when-not removed? (<sub [:branding/image-url type]))]
    [:div.mt-5
     [:label.bx--label label]
     [image-upload tr type]
     (when img-url
       [:img {:style {:width "50%"}
              :src   img-url}])]))

(defn landing-background-settings [tr group]
  (let [hue      (or (<sub [::value-in-editor :group/branding-background-hue])
                     (:group/branding-background-hue group)
                     270) ; corresponds almost exactly to the default background color
        bg-color (color/hsv->rgb hue 0.061 0.961)
        removed? (<sub [::image-has-been-removed? :background])
        img-url  (or (when-not removed? (<sub [:branding/image-url :background]))
                     "/asset/img/Cloudpermit_background.svg")]
    [:<>
     [ui/help (tr [:group/branding-bg-help])]
     (cc/slider {:label-text (tr [:group/branding-background-hue])
                 :min        0
                 :max        360
                 :value      hue
                 :on-change  #(>evt [::set-value-in-editor :group/branding-background-hue (futil/oget % :value)])})
     [:div.mt-5
      [:label.bx--label (tr [:group/branding-landing-background])]
      [image-upload tr :background {:button-i18n-key :group/branding-landing-background-upload
                                    :remove-i18n-key :group/branding-landing-background-remove
                                    :group           group}]
      [:h5.mt-2 (tr [:group/branding-preview])]
      [:div {:style {:width               "100%"
                     :height              "200px"
                     :margin-top          "0.5rem"
                     :background-color    bg-color
                     :background-image    (str "url(" img-url ")")
                     :background-size     "cover"
                     :background-position "center"}}]]]))

(defn landing-header-background-settings [tr]
  [image-upload-with-preview tr :landing-background (tr [:group/branding-landing-header-background])])

(defn- branding-login-info-title [tr label]
  [ui/text-input {:id         :group/branding-login-info-title
                  :class      "my-5"
                  :label-text (tr [label])
                  :value      (<sub [::value-in-editor-or-group :group/branding-login-info-title])
                  :on-change  #(rf/dispatch-sync [::set-value-in-editor :group/branding-login-info-title (futil/tval %)])}])

(defn- branding-login-info [tr label]
  [:<>
   [:label.bx--label (tr [label])]
   [ui-rt/editor {:id            (str "branding-login-info-editor-" (<sub [::editor-generation]))
                  :initial-value (<sub [::value-in-editor-or-group :group/branding-login-info])
                  :on-change     #(rf/dispatch-sync [::set-value-in-editor :group/branding-login-info %])}]])

;; -- Call to action editor UI --------------------------------------------------

(def call-to-action-icons
  (select-keys
    carbon-icons/icons
    [:icon/clock
     :icon/calendar
     :icon/location
     :icon/info
     :icon/bell
     :icon/phone
     :icon/document]))

(defn- cta-icon-button [current-icon-key icon-key icon on-change]
  (let [selected? (= current-icon-key icon-key)]
    [:button.p-2.border.rounded.cursor-pointer
     {:class    (if selected? "border-purple-600 bg-purple-100" "border-gray-300")
      :style    (when selected? {:border-width "2px"})
      :on-click #(on-change icon-key)}
     [:> icon]]))

(defn- cta-icon-selector [current-icon-key on-change tr]
  [:div
   [:label.bx--label (tr [:group/branding-cta-icon])]
   [:div.flex.gap-2
    (futil/for-all [[icon-key icon] call-to-action-icons]
      ^{:key icon-key}
      [cta-icon-button current-icon-key icon-key icon on-change])]])

(defn- cta-editor-fields [tr idx cta]
  (let [gen (<sub [::editor-generation])]
    [:div
     [:hr.my-4]
     [:div.flex.gap-4.items-end
      [:div.flex-1
       [ui-i18n/localized-string-edit
        {:id                  (str "cta-title-" idx)
         :label-text          (tr [:group/branding-cta-title])
         :strings             (or (:call-to-action/title cta) {})
         :allowed-locales-sub ::gas/allowed-locales
         :required            true
         :auto-focus          false
         :on-change           #(rf/dispatch-sync [::set-cta-field idx :call-to-action/title %])}]]
      [:div.flex-1
       [ui-i18n/localized-string-edit
        {:id                  (str "cta-subtitle-" idx)
         :label-text          (tr [:group/branding-cta-subtitle])
         :strings             (or (:call-to-action/subtitle cta) {})
         :allowed-locales-sub ::gas/allowed-locales
         :required            true
         :auto-focus          false
         :on-change           #(rf/dispatch-sync [::set-cta-field idx :call-to-action/subtitle %])}]]
      [:div.flex-shrink-0
       [cta-icon-selector
        (:call-to-action/icon cta)
        #(>evt [::set-cta-field idx :call-to-action/icon %])
        tr]]]
     [:div.mt-4
      [ui-i18n/localized-rich-text-editor
       {:id                  (str "cta-content-" idx "-" gen)
        :label-text          (tr [:group/branding-cta-content])
        :strings             (or (:call-to-action/content cta) {})
        :allowed-locales-sub ::gas/allowed-locales
        :required?           true
        :tr                  tr
        :on-change           #(rf/dispatch-sync [::set-cta-field idx :call-to-action/content %])}]]]))

(defn- cta-slot-editor [tr idx cta]
  [:div.border.border-solid.border-gray-500.rounded.p-4.mb-4
   [:h5.font-bold (tr [:group/branding-cta-button] [(inc idx)])]
   (cc/toggle {:id        (str "cta-toggle-" idx)
               :toggled   (boolean (:enabled? cta))
               :on-toggle #(>evt [::toggle-cta-slot idx %])
               :size      :sm})
   (when (:enabled? cta)
     [cta-editor-fields tr idx cta])])

(defn- dynamic-call-to-action-kind-label
  [kind]
  (keyword "group" (str "branding-cta-dynamic-" (name kind))))

(defn dynamic-calls-to-action-settings
  [tr]
  (let [enabled-kinds (<sub [::dynamic-ctas-effective])]
    [:div.mt-5
     (futil/for-all [kind (sort-by name cta-model/dynamic-call-to-action-kinds)]
       ^{:key kind}
       [:div.border.border-solid.border-gray-500.rounded.p-4.mb-4
        [:h5.font-bold (tr [(dynamic-call-to-action-kind-label kind)])]
        (cc/toggle {:id        (str "dynamic-cta-toggle-" (name kind))
                    :toggled   (contains? enabled-kinds kind)
                    :on-toggle #(>evt [::toggle-dynamic-cta enabled-kinds kind %])
                    :size      :sm})])]))

(defn calls-to-action-settings
  "CTA editor cards, shown under the landing page settings section."
  [tr]
  (when-some [draft (<sub [::ctas-draft])]
    [:div.mt-5
     [ui/help (tr [:group/branding-cta-settings-help])]
     (futil/for-all-indexed [[idx cta] draft]
       ^{:key idx}
       [cta-slot-editor tr idx cta])
     [ui/help (tr [:group/branding-cta-dynamic-settings-help])]
     [dynamic-calls-to-action-settings tr]]))

(defn- custom-link-icon-button [current-icon icon-key img-path on-change]
  (let [selected? (= current-icon icon-key)]
    [:button.p-2.border.rounded.cursor-pointer.bg-white
     {:class    (if selected? "border-purple-600" "border-gray-300")
      :style    (when selected? {:border-width "2px"})
      :on-click #(on-change icon-key)}
     [:img.w-14.h-14 {:src img-path :alt (name icon-key)}]]))

(defn- custom-link-icon-selector [current-icon on-change tr]
  [:div
   [:label.bx--label (tr [:custom-link/icon])]
   [:div.flex.gap-2
    (futil/for-all [[icon-key img-path] custom-link-model/custom-link-icon->img]
      ^{:key icon-key}
      [custom-link-icon-button current-icon icon-key img-path on-change])]])

(defn- custom-link-editor [tr idx link]
  (let [gen          (<sub [::editor-generation])
        url          (:custom-link/url link)
        url-missing? (str/blank? url)
        url-invalid? (and (not url-missing?)
                          (not (validators/valid-uri? url)))]
    [:div.border.rounded.p-4.mb-3
     [:div.flex.justify-between.items-center
      [:h5.font-bold (str (tr [:custom-link/admin-title]) " " (inc idx))]
      [ui/button {:kind     :ghost
                  :size     :small
                  :on-click #(>evt [::remove-custom-link idx])}
       (tr [:custom-link/remove])]]
     [:div.flex.flex-col.gap-4.mt-4
      [custom-link-icon-selector
       (:custom-link/icon link)
       #(>evt [::set-custom-link-field idx :custom-link/icon %])
       tr]
      [ui/text-input {:id           (str "custom-link-url-" idx)
                      :label-text   (tr [:custom-link/url])
                      :value        (or url "")
                      :required     true
                      :invalid      (or url-missing? url-invalid?)
                      :invalid-text (when url-invalid? (tr [:custom-link/url-invalid]))
                      :on-change    #(>evt [::set-custom-link-field idx :custom-link/url (futil/tval %)])}]
      [ui-i18n/localized-string-edit
       {:id                  (str "custom-link-titles-" idx)
        :label-text          (tr [:custom-link/title])
        :strings             (or (:custom-link/titles link) {})
        :allowed-locales-sub ::gas/allowed-locales
        :required            true
        :auto-focus          false
        :on-change           #(>evt [::set-custom-link-field idx :custom-link/titles %])}]
      [ui-i18n/localized-string-edit
       {:id                  (str "custom-link-descriptions-" idx "-" gen)
        :strings             (or (:custom-link/descriptions link) {})
        :label-text          (tr [:custom-link/description])
        :auto-focus          false
        :allowed-locales-sub ::gas/allowed-locales
        :on-change           #(>evt [::set-custom-link-field idx :custom-link/descriptions %])}]]]))

(defn custom-link-settings
  "Dynamic custom-link editor that manages up to 3 custom links as component entities."
  [tr]
  (let [draft (or (<sub [::custom-links-draft]) [])]
    [:div.my-5
     [:h4 (tr [:custom-link/section-title])]
     [ui/help (tr [:custom-link/admin-help])]
     (futil/for-all [[idx link] (map-indexed vector draft)]
       ^{:key (or (:db/id link) (str "new-" idx))}
       [custom-link-editor tr idx link])
     (when (< (count draft) max-custom-links)
       [:div.mt-3
        [ui/button {:kind     :tertiary
                    :size     :small
                    :on-click #(>evt [::add-custom-link])}
         (tr [:custom-link/add])]])]))

(defn path-id-editor [tr group]
  (let [path-id           (<sub [::uri-path-id])
        effective-path-id (branding-model/or-default (or path-id (:group/uri-path-id group)))
        admin?            (<sub [:session/admin?])
        invalid-path-id?  (or (contains? branding-model/banned-brandings path-id)
                              (and (some? path-id)
                                   (str/blank? path-id)))
        landing-page-url  (str (.-origin js/window.location) "/" effective-path-id)]
    [:div.flex.flex-col.gap-4
     [ui/help (tr [:group/uri-path-id-info])]
     [:div.flex.flex-col.md:flex-row.md:items-end.gap-4.w-full
      [:div.flex-1
       [ui/text-input {:id         :group/uri-path-id
                       :label-text (tr [:group/uri-path-id])
                       :invalid    invalid-path-id?
                       :disabled   (not admin?)
                       :value      (or path-id
                                       (:group/uri-path-id group))
                       :on-change  #(rf/dispatch-sync [::set-uri-path-id (futil/tval %)])}]]
      (when admin?
        [:div.flex.gap-2.items-end
         [ui-action-buttons/save-cancel-buttons
          {:on-cancel      #(>evt [::set-uri-path-id nil])
           :on-save        #(>evt [::save-uri-path-id (:db/id group) path-id])
           :save-disabled? (or (nil? path-id)
                               invalid-path-id?)
           :size           :small
           :tr             tr}]
         [ui/modal-confirmation-button
          (p/assoc-if
            {:trigger-button-text (tr [:group/reset-branding-uri])
             :trigger-button-opts {:kind :danger
                                   :size :small}
             :tr                  tr
             :id                  "remove-uri-path-id"
             :on-yes-click        #(>evt [::save-uri-path-id (:db/id group) nil])
             :disabled?           (not (:group/uri-path-id group))}
            :modal-title (tr [:are-you-sure]))
          (tr [:group/reset-branding-uri-confirm])]])]
     [:div.flex.gap-4
      [:a.text-blue-600.hover:underline {:href   landing-page-url
                                         :target "_blank"
                                         :rel    "noopener noreferrer"}
       (tr [:group/landing-page-link])]
      [:a.text-blue-600.hover:underline {:href   (str landing-page-url "/login")
                                         :target "_blank"
                                         :rel    "noopener noreferrer"}
       (tr [:group/login-page-link])]]]))

(defn brand-header-preview [tr group]
  (let [bg-color   (or (<sub [::value-in-editor-or-group :group/branding-banner-bg-color]) "#191147")
        fg-color   (or (<sub [::value-in-editor-or-group :group/branding-banner-fg-color]) "#ffffff")
        logo-url   (<sub [:branding/image-url :logo])
        lang       (<sub [::ui-i18n/editor-language ::gas/allowed-locales])
        title-text (some-> (or (<sub [::header-title])
                               (i18n-model/strings-map (:group/branding-header-titles group)))
                           (i18n-model/get-text-from-entities-or-map lang))
        btn-style  {:color fg-color :cursor "default"}]
    [:section.flex.items-center.w-full.rounded
     {:style {:background-color bg-color
              :color            fg-color
              :min-height       "60px"}}
     ;; Logo
     [:img {:src   (or logo-url "/asset/img/Cloudpermit_logo_RGB_white.svg")
            :alt   ""
            :style {:max-height "50px" :max-width "100%"}}]
     ;; Header title
     (when (seq title-text)
       [:h1.font-bold.truncate.px-2.self-center.text-lg {:style {:color fg-color}} title-text])
     ;; Spacer
     [:div.flex-1]
     ;; Mock buttons (inert)
     [:div.flex.items-center.self-stretch
      [:div.flex.items-center.gap-1.px-2 {:style btn-style}
       (cc/icon {:icon i/world :description "" :fill fg-color :aria-hidden true})
       [:span.text-sm.whitespace-nowrap.uppercase (or (some-> lang name) "EN")]]
      [:div.flex.items-center.gap-1.px-2 {:style btn-style}
       (cc/icon {:icon i/circle-question :description "" :fill fg-color :aria-hidden true})
       [:span.text-sm.whitespace-nowrap.hidden.md:inline (tr [:support-text])]]
      [:div.flex.items-center.gap-1.px-2 {:style btn-style}
       (cc/icon {:icon i/user :description "" :fill fg-color :aria-hidden true})
       [:span.text-sm.whitespace-nowrap.hidden.md:inline (tr [:user/user])]]]]))

(defn brand-header-settings [tr group]
  (let [header-title (or (<sub [::header-title])
                         (i18n-model/strings-map (:group/branding-header-titles group)))]
    [:<>
     [ui/help (tr [:group/branding-brand-header-help])]
     [header-color-settings tr]
     [:div.my-5
      [ui-i18n/localized-string-edit
       {:id                  "branding-header-titles"
        :label-text          (tr [:group/branding-header-titles])
        :strings             header-title
        :allowed-locales-sub ::gas/allowed-locales
        :auto-focus          false
        :on-change           #(>evt [::set-header-title %])}]]
     [logo-upload tr group]
     [:h5.mt-2 (tr [:group/branding-preview])]
     [brand-header-preview tr group]]))

(defn large-header-preview [tr group]
  (let [logo-url      (<sub [:branding/image-url :logo])
        bg-url        (or (<sub [:branding/image-url :landing-background])
                          "/asset/img/Header-image.png")
        lang          (<sub [::ui-i18n/editor-language ::gas/allowed-locales])
        title-text    (some-> (or (<sub [::landing-page-title])
                                  (i18n-model/strings-map (:group/branding-landing-page-titles group)))
                              (i18n-model/get-text-from-entities-or-map lang))
        subtitle-text (some-> (or (<sub [::landing-page-subtitle])
                                  (i18n-model/strings-map (:group/branding-landing-page-subtitles group)))
                              (i18n-model/get-text-from-entities-or-map lang))]
    [:section.landing-header.relative.flex.items-center.justify-center.rounded
     {:style {:min-height          "200px"
              :background-image    (str "url(" bg-url ")")
              :background-size     "cover"
              :background-position "center"
              :background-repeat   "no-repeat"}}
     [:div.flex.flex-col.lg:flex-row.w-full.h-full.px-6.py-4.lg:absolute.lg:items-center
      ;; Logo + header text
      [:div.flex.flex-1.items-center.gap-4.min-w-0
       [:img.flex-shrink-0.w-28.lg:w-40
        {:src (or logo-url "/asset/img/Cloudpermit_logo_RGB_white.svg")
         :alt ""}]
       [:div.flex.flex-col
        (when (seq title-text)
          [:h1.text-white.landing-header__header-text.m-0 title-text])
        (when (seq subtitle-text)
          [:h2.text-white.landing-header__header-text.m-0.text-lg subtitle-text])]]
      ;; Mock action buttons (inert, matching landing-header-button styling)
      [:div.flex.items-center.w-full.lg:w-auto.lg:border-t-0.mt-4.lg:gap-2.lg:pt-0.lg:self-end
       {:class "border-t border-white/20"}
       ;; Mock language button
       [:div.flex.items-center.justify-center.gap-2.flex-1.py-3.lg:flex-initial.lg:px-3.lg:py-2.lg:rounded.lg:bg-ui-night-sky-100.text-white.text-sm.whitespace-nowrap
        {:style {:cursor "default"}}
        (cc/icon {:icon i/world :description "" :fill "currentColor" :aria-hidden true})
        [:span.uppercase (or (some-> lang name) "EN")]]
       ;; Mock help button
       [:div.flex.items-center.justify-center.gap-2.flex-1.py-3.lg:flex-initial.lg:px-3.lg:py-2.lg:rounded.lg:bg-ui-night-sky-100.text-white.text-sm.whitespace-nowrap
        {:style {:cursor "default"}}
        (cc/icon {:icon i/circle-question :description "" :fill "currentColor" :aria-hidden true})
        [:span (tr [:need-help])]]
       ;; Mock sign-in button
       [:div.flex.items-center.justify-center.gap-2.flex-1.py-3.lg:flex-initial.lg:px-3.lg:py-2.lg:rounded.lg:bg-ui-night-sky-100.text-white.text-sm.whitespace-nowrap
        {:style {:cursor "default"}}
        (cc/icon {:icon i/log-in :description "" :fill "currentColor" :aria-hidden true})
        [:span (tr [:login-new/sign-in])]]]]]))

(defn large-header-settings [tr group]
  (let [large-header? (let [edited (<sub [::value-in-editor :group/branding-landing-page-large-header?])
                            stored (:group/branding-landing-page-large-header? group)]
                        (if (some? edited) edited (if (some? stored) stored true)))
        title         (or (<sub [::landing-page-title])
                          (i18n-model/strings-map (:group/branding-landing-page-titles group)))
        subtitle      (or (<sub [::landing-page-subtitle])
                          (i18n-model/strings-map (:group/branding-landing-page-subtitles group)))]
    [:section
     [:h5 (tr [:group/branding-large-header-section])]
     [ui/help (tr [:group/branding-large-header-help])]
     [:div.my-5
      (cc/toggle {:id        "branding-landing-page-large-header"
                  :toggled   large-header?
                  :on-toggle #(>evt [::set-value-in-editor :group/branding-landing-page-large-header? %])
                  :size      :sm})]
     (when large-header?
       [:<>
        [:div.my-5
         [ui-i18n/localized-string-edit
          {:id                  "branding-landing-page-titles"
           :label-text          (tr [:group/branding-landing-page-titles])
           :strings             title
           :allowed-locales-sub ::gas/allowed-locales
           :auto-focus          false
           :on-change           #(>evt [::set-landing-page-title %])}]]
        [:div.my-5
         [ui-i18n/localized-string-edit
          {:id                  "branding-landing-page-subtitles"
           :label-text          (tr [:group/branding-landing-page-subtitles])
           :strings             subtitle
           :allowed-locales-sub ::gas/allowed-locales
           :auto-focus          false
           :on-change           #(>evt [::set-landing-page-subtitle %])}]]
        ; TODO: Separate logo upload for the large version of the header? Using the same logo for now.
        [image-upload tr :landing-background {:button-i18n-key :group/branding-landing-header-background-upload}]
        [:h5.mt-2 (tr [:group/branding-preview])]
        [large-header-preview tr group]])]))

(defn header-settings [tr group]
  [:<>
   [brand-header-settings tr group]
   [large-header-settings tr group]])

(defn settings-section [tr title lang-selector? & content]
  (into [:section
         [:div.flex.justify-between.items-center.mb-3
          [:h4 (tr [title])]
          (when lang-selector?
            [:div.flex.items-center.gap-2
             [:span (str (tr [:language/for-language]) ":")]
             [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales
                                              :dropdown-class      ""
                                              :hide-label          true}]])]]
        content))

(defn section [tr]
  (r/with-let [*initialized-group-id (r/atom nil)]
    (let [group         (<sub [::gas/group])
          group-id      (:db/id group)
          superdomain?  (= :group.type/superdomain (:group/type group))
          ctas-group-id (<sub [::ctas-group-id])]
      (when (and group-id
                 (not= @*initialized-group-id group-id))
        (reset! *initialized-group-id group-id)
        (when-let [branding (:group/uri-path-id group)]
          (>evt [:branding/force-branding branding]))
        (>evt [::init-dynamic-ctas (:group/dynamic-calls-to-action group)])
        (when (not= group-id ctas-group-id)
          (>evt [::fetch-ctas group-id]))
        (when-let [config (<sub [:branding/config])]
          (>evt [::init-custom-links-draft (:custom-links config)])))
      [:<>
       (if superdomain?
         [:section
          [path-id-editor tr group]
          [:div.mt-4
           [:h4 (tr [:group/branding-header-settings])]
           [logo-upload tr group]
           [landing-header-background-settings tr]
           [landing-background-settings tr group]]]

         [:<>
          [settings-section tr :group/branding-section-landing-page-url false
           [path-id-editor tr group]]

          [:hr]

          [settings-section tr :group/branding-section-brand-header true
           [header-settings tr group]]

          [settings-section tr :group/branding-section-custom-sub-header true
           [calls-to-action-settings tr]]

          [settings-section tr :group/branding-section-background false
           [landing-background-settings tr group]]

          [settings-section tr :group/branding-section-landing-page-content false
           [ui/help (tr [:group/branding-landing-info-help])]
           [branding-login-info-title tr :group/branding-landing-info-title]
           [branding-login-info tr :group/branding-landing-info]
           [custom-link-settings tr]]])

       ; Controls
       [:div.flex.items-center.justify-between
        [delete-branding-button tr group]
        [ui-action-buttons/save-cancel-buttons
         {:on-cancel      #(>evt [::cancel-edits])
          :on-save        #(>evt [::save-all group-id])
          :save-disabled? (or (and (<sub [::custom-links-edited?])
                                   (not (<sub [::custom-links-valid?])))
                              (not (or (<sub [::values-have-been-edited?])
                                       (<sub [::header-texts-have-been-edited?])
                                       (<sub [::images-have-been-added?])
                                       (<sub [::images-have-been-removed?])
                                       (<sub [::ctas-edited?])
                                       (<sub [::custom-links-edited?])
                                       (<sub [::dynamic-ctas-edited?]))))
          :size           :small
          :tr             tr}]]])))
